import { inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, map, distinctUntilChanged, shareReplay, takeUntil, Subject, of, tap, catchError, switchMap, filter, forkJoin, firstValueFrom, NEVER } from 'rxjs';
import { User } from './models/user.model';
import { AuthService } from './auth/auth.service';
import { MeService } from './services/me.service';
import { MeResponse } from './models/me-response.model';
import { generateInitials } from '../shared/utils/user-utils';
import { Organization } from './models/organization.model';
import { NavigationEnd, Router } from '@angular/router';
import { getOrganizationFromPath } from '../shared/utils/route-utils';
import { ErrorService } from './errors/error.service';
import { ErrorCode, ResourceType } from './errors/error.model';
import { NON_ORG_ROUTES } from './portal.service';
import { Role } from './auth/models/role';
import { TenantService } from '../modules/admin/tenants/services/tenant.service';
import { SettingsResponse } from './models/settings-response.model';
import { Setting } from './models/settings.model';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { UnitService } from './units/unit.service';
import { UnitSystem } from './units/units';

/** Upper bound for the one-shot staff "all tenants" fetch — consumers search
 *  client-side, so we want the whole directory, not a page. */
const ALL_TENANTS_PAGE_SIZE = 500;

export interface ContextState {
  user: User | null;
  organization: Organization | null;
  organizations: Organization[];
  roles: string[];
  settings: Record<string, Setting>;
}

const ORGANIZATION_STORAGE_KEY = 'organization';

const ORG_REDIRECT_ATTEMPTED_KEY = 'orgRedirectAttempted';

@Injectable({
  providedIn: 'root'
})
export class ContextService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private readonly authService = inject(AuthService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly meService = inject(MeService);
  private readonly errorService = inject(ErrorService);
  private readonly tenantService = inject(TenantService);
  private readonly unitService = inject(UnitService);

  private readonly stateSubject = new BehaviorSubject<ContextState>({
    user: null,
    organization: null,
    organizations: [],
    roles: [],
    settings: {},
  });

  public readonly state$: Observable<ContextState>  = this.stateSubject.asObservable();

  public readonly user$: Observable<User | null> = this.state$.pipe(
    map(state => state.user),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly organization$: Observable<Organization | null> = this.state$.pipe(
    map(state => state.organization),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly organizations$: Observable<Organization[]> = this.state$.pipe(
    map(state => state.organizations),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly roles$: Observable<string[]> = this.state$.pipe(
    map(state => state.roles),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // True once the context has been populated from /me (or the JWT fallback).
  // user/organization/roles are all set in the same updateContext() call, so
  // user presence is a reliable "context loaded" signal — and, unlike
  // organization, it holds for org-less super-admins too.
  public readonly loaded$: Observable<boolean> = this.state$.pipe(
    map(state => state.user !== null),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly weatherLocation$: Observable<string | null> = this.state$.pipe(
    map(state => {
      const raw = state.settings['weather.location']?.value;
      return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : null;
    }),
    distinctUntilChanged(),
  );

  load(): Promise<void> {
    return firstValueFrom(
      this.oidcSecurityService.checkAuth().pipe(
        switchMap(({ isAuthenticated }) => {
          if (!isAuthenticated) {
            this.authService.login();
            return NEVER;
          }
          return this.redirectToRememberedOrgIfNeeded().pipe(
            switchMap(redirecting => redirecting ? NEVER : this.loadUserData())
          );
        }),
        catchError(() => {
          const url = new URL(window.location.href);
          for (const param of ['code', 'state', 'session_state', 'iss']) {
            url.searchParams.delete(param);
          }
          window.history.replaceState(null, '', url.toString());
          this.authService.login();
          return NEVER;
        }),
      )
    );
  }

  constructor(private router: Router) {
    this.watchRouterForOrganizationChanges();
  }

  private watchRouterForOrganizationChanges(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      switchMap(() => this.organization$.pipe(
        filter(org => org !== null),
        map(() => this.extractOrganizationFromUrl())
      )),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(urlOrgName => {
        const organization = this.stateSubject.value.organization;

        // Non-org top-level routes (the /admin back-office, error pages) aren't
        // organization-scoped — their first URL segment is not an org name, so
        // skip the org-matching logic that would otherwise redirect to
        // not-found?resource=organization.
        if (NON_ORG_ROUTES.has(urlOrgName)) {
          return;
        }

        // If URL org matches user's org, no action needed
        if (urlOrgName && organization && urlOrgName === organization.name)  {
          return;
        }

        if (this.belongsToOrganization(urlOrgName)) {
          const switchingOrganization = this.stateSubject.value.organizations
              .find(organization => organization.name === urlOrgName);

          if (switchingOrganization) {
            this.authService.switchOrganization(switchingOrganization!.id);
            return;
          }
        }

        // URL org doesn't match user's org - redirect to correct org
        this.errorService.notFound(ResourceType.ORGANIZATION);
      });
  }

  private extractOrganizationFromUrl(): string {
    return getOrganizationFromPath(this.router);
  }

  private redirectToRememberedOrgIfNeeded(): Observable<boolean> {
    const rememberedOrgId = this.authService.getRememberedOrganizationId();
    if (!rememberedOrgId) {
      return of(false);
    }
    if (sessionStorage.getItem(ORG_REDIRECT_ATTEMPTED_KEY)) {
      return of(false);
    }
    return this.authService.getOrganizationFromToken().pipe(
      map(tokenOrg => {
        if (tokenOrg?.id === rememberedOrgId) {
          return false;
        }
        sessionStorage.setItem(ORG_REDIRECT_ATTEMPTED_KEY, '1');
        this.authService.switchOrganization(rememberedOrgId);
        return true;
      })
    );
  }

  private loadUserData(): Observable<void> {
    return forkJoin({
      me: this.meService.getMe(),
      settings: this.meService.getSettings()
    }).pipe(
      switchMap(({ me, settings }) =>
        this.authService.getOrganizationFromToken().pipe(
          map(organization =>
            this.processApiResponse(me, settings, organization)
          )
        )
      ),
      switchMap(state => this.withAllOrganizationsForStaff(state)),
      catchError(error => {
        console.warn('/me endpoint failed, falling back to JWT:', error);
        return this.loadFromJwtFallback();
      }),
      tap(state => {
        this.updateContext(state);
        if (state.settings) {
          this.applyUnitSystem(state.settings);
        }
        if (state.organization) {
          localStorage.setItem(ORGANIZATION_STORAGE_KEY, JSON.stringify(state.organization));
        } else {
          this.errorService.navigateToError(ErrorCode.NO_ORGANIZATION);
        }
      }),
      map(() => void 0)
    );
  }

  private processApiResponse(response: MeResponse, settings: SettingsResponse[], organization: Organization | null): Partial<ContextState> {
    const user = response.user;
    if (user && !user.initials && user.firstName && user.lastName) {
      user.initials = generateInitials(user.firstName, user.lastName);
    }

    const settingsMap: Record<string, Setting> = {};

    for (const s of settings) {
      const setting: Setting  = { value: s.value };
      settingsMap[s.key] = setting;
    }

    return {
      user,
      organization,
      organizations: response.organizations ?? [],
      roles: response.roles ?? [],
      settings: settingsMap
    };
  }

  /**
   * Staff (super-admin/support) can enter any tenant, so their organizations
   * list is the full enabled-organization directory, not just their
   * memberships. This is what makes OrganizationGuard and the URL watcher
   * treat an existing-but-foreign org as switchable instead of not-found, and
   * what feeds the topbar switcher its full list. Falls back to memberships
   * when the directory fetch fails, so staff never end up below /me.
   */
  private withAllOrganizationsForStaff(state: Partial<ContextState>): Observable<Partial<ContextState>> {
    const roles = state.roles ?? [];
    if (!roles.includes(Role.SUPER_ADMIN) && !roles.includes(Role.SUPPORT)) {
      return of(state);
    }
    return this.tenantService.getTenants(0, ALL_TENANTS_PAGE_SIZE, { enabled: true }).pipe(
      map(response => ({
        ...state,
        organizations: response.data.map((tenant): Organization => ({
          id: tenant.id,
          name: tenant.name,
          alias: tenant.alias,
        })),
      })),
      catchError(() => of(state))
    );
  }

  private loadFromJwtFallback(): Observable<Partial<ContextState>> {
    return forkJoin({
      user: this.authService.getUserFromToken(),
      organization: this.authService.getOrganizationFromToken()
    }).pipe(
      map(({ user, organization }) => {
        if (user && !user.initials && user.firstName && user.lastName) {
          user.initials = generateInitials(user.firstName, user.lastName);
        }
        return {
          user,
          organization,
          organizations: organization ? [organization] : [],
          roles: [],
        };
      })
    );
  }

  private clearContext(): void {
    this.updateContext({
      user: null,
      organization: null,
      organizations: [],
      roles: [],
    });
    localStorage.removeItem(ORGANIZATION_STORAGE_KEY);
  }

  getOrganization(): Organization | null {
    return this.stateSubject.value.organization;
  }

  getOrganizations(): Organization[] {
    return this.stateSubject.value.organizations;
  }

  /**
   * The organization last persisted to localStorage, validated against the
   * user's current memberships — a stale entry (revoked membership, deleted
   * org) is discarded rather than trusted. Matched by id and resolved to the
   * fresh membership object, so a renamed org yields its current name.
   */
  getPersistedOrganization(): Organization | null {
    try {
      const raw = localStorage.getItem(ORGANIZATION_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const stored = JSON.parse(raw) as Organization;
      return this.stateSubject.value.organizations
        .find(org => org.id === stored.id) ?? null;
    } catch {
      return null;
    }
  }

  belongsToOrganization(orgName: string): boolean {
    return (this.stateSubject.value.organizations ?? []).some(org => org.name === orgName);
  }

  getRoles(): string[] {
    return this.stateSubject.value.roles;
  }

  hasRole(role: string): boolean {
    return this.stateSubject.value.roles.includes(role);
  }

  hasAnyRole(...roles: string[]): boolean {
    const userRoles = this.stateSubject.value.roles;
    return roles.some(role => userRoles.includes(role));
  }

  hasAllRoles(...roles: string[]): boolean {
    const userRoles = this.stateSubject.value.roles;
    return roles.every(role => userRoles.includes(role));
  }

  getSetting(settingKey: string): Setting | undefined {
    return this.stateSubject.value.settings[settingKey];
  }

  patchSettings(patch: Record<string, unknown>): void {
    const current = this.stateSubject.value.settings;
    const updated = { ...current };
    for (const [key, value] of Object.entries(patch)) {
      updated[key] = { ...current[key], value };
    }
    this.updateContext({ settings: updated });
    this.applyUnitSystem(updated);
  }

  private applyUnitSystem(settings: Record<string, Setting>): void {
    const raw = settings['weather.units']?.value;
    const system: UnitSystem = raw === 'imperial' ? 'imperial' : 'metric';
    this.unitService.setSystem(system);
  }

  private updateContext(partial: Partial<ContextState>): void {
    this.stateSubject.next({
      ...this.stateSubject.value,
      ...partial
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stateSubject.complete();
  }
}
